import { useQueryWithSSRErrors } from "@/shared/hooks";
import { ProductContentsQuery } from "../graphql";
import type {
  ProductContentsQueryType,
  ProductContentsQueryVariablesType,
  ContentTypeType,
  StreamStatusType,
} from "@/types/graphql/site.generated";

export interface UseProductContentsOptions {
  productId?: string;
  type?: ContentTypeType;
  /** Полки стримов на странице продукта: STARTED «идут сейчас», PENDING «скоро». */
  streamStatus?: StreamStatusType;
  limit?: number;
  skip?: boolean;
}

// Состав продукта (Product.contents) — отдельный хук, а не useContents:
// Query.contents (SiteContentsFilterInput) не умеет фильтровать по продукту,
// состав продукта отдаёт только Product.contents(type, page, rowsPerPage)
// (см. backend/src/domains/products/graphql/site/schema.graphql).
export function useProductContents(options: UseProductContentsOptions = {}) {
  const { productId, type, streamStatus, limit = 12, skip = false } = options;

  const variables: ProductContentsQueryVariablesType = {
    id: productId as string,
    type,
    streamStatus,
    page: 0,
    rowsPerPage: limit,
  };

  const { data, loading, error, fetchMore, called } = useQueryWithSSRErrors<
    ProductContentsQueryType,
    ProductContentsQueryVariablesType
  >(ProductContentsQuery, {
    variables,
    skip: skip || !productId,
    notifyOnNetworkStatusChange: true,
  });

  const contents = data?.product?.contents?.nodes ?? [];
  const count = data?.product?.contents?.count ?? 0;
  const hasMore = contents.length < count;

  const loadMore = () => {
    if (loading || !hasMore) return;
    const nextPage = Math.floor(contents.length / limit);
    void fetchMore({ variables: { ...variables, page: nextPage } });
  };

  return { contents, count, loading, error, hasMore, loadMore, called };
}
